/**
 * `chainSegments` — cut a resolution's recorded steps into the pieces the board plays one after
 * another (`chained-resolution-sequence` Phase 2, `decisions/0210`).
 *
 * A resolution with no `link` step is one segment, and the board plays it exactly as it always
 * has. A chained resolution (the game-night chain: Bret's Shotgun hits the Berserker, whose
 * Aggression forces a Chain Axe swing at the Elite Cataphract) is one segment per link: the
 * primary action, then each link from its marker to the next. Each segment after the first opens
 * with a caption over its actor, moves the focus ring, and throws its own dice when the board
 * reaches it, so the Chain Axe's d20 does not land while Bret's shot is still in the air.
 *
 * The cut is by recorded order alone. The partition property follows: concatenating the segments'
 * steps (and their link markers) gives back the record, so `applyResolved(prev, steps) === next`
 * holds whatever the board does with the pieces.
 */
import type { LinkStep, ResolvedStep, TableCheckStep } from "@lancer-vtt/domain";

/** One piece of a resolution the board plays in turn. */
export type ChainSegment = {
  /** Position in the resolution (`0` is the primary action); the per-segment dice and hold keys' suffix. */
  index: number;
  /** The `link` step that opened this segment; absent for the primary action and a hold. */
  link?: LinkStep;
  /**
   * The roll this segment opens on, held until someone presses Roll it (`chained-resolution-sequence`
   * Phase 3): a structure or overheat table check (`table_check`, its first step), or `"start"` for
   * a GM "hold next" on the whole resolution. Absent for a segment that plays straight through.
   */
  hold?: TableCheckStep | "start";
  /** The segment's own steps, its `link` marker excluded (and its reactions' steps, below). */
  steps: ResolvedStep[];
  /**
   * The reactions that interrupted this segment's attack (`chained-resolution-sequence` 4.3): a
   * Brace, a Rebound, a target's on-damage clause, each from its `reaction` link to its `resume`.
   * The steps after the `resume` stay in {@link steps}, so the attack compiles as one attack.
   */
  reactions: ChainReaction[];
};

/** One reaction inside a segment: its link, its steps, and its own dice key's index. */
export type ChainReaction = { link: LinkStep; steps: ResolvedStep[]; index: number };

/**
 * Where to hold. `checks`: before every structure and overheat table check (the auto-hold,
 * `decisions/0210` item 3). `start`: before the whole resolution (the GM's "hold next").
 */
export type HoldPolicy = { checks: boolean; start: boolean };

/** No holds: the board plays the chain straight through. */
export const NO_HOLDS: HoldPolicy = { checks: false, start: false };

/**
 * Cut `steps` at every `forced_attack`/`drone_field` link, and before each held roll the policy
 * names; a `reaction` link's steps up to its `resume` become a reaction of the segment it
 * interrupted (`narrate.ts`'s `chainParts` is the same cut, for the narrator and the card). Always
 * returns at least one segment; a `start` hold marks segment 0.
 */
export function chainSegments(steps: readonly ResolvedStep[], policy: HoldPolicy = NO_HOLDS): ChainSegment[] {
  const segments: ChainSegment[] = [{ index: 0, ...(policy.start ? { hold: "start" as const } : {}), steps: [], reactions: [] }];
  let reaction: ChainReaction | undefined;
  let reactions = 0;
  for (const step of steps) {
    const current = segments[segments.length - 1]!;
    if (step.op === "link" && step.cause === "reaction") {
      reaction = { link: step, steps: [], index: reactions++ };
      current.reactions.push(reaction);
    } else if (step.op === "link" && step.cause === "resume") reaction = undefined;
    else if (step.op === "link") {
      reaction = undefined;
      segments.push({ index: segments.length, link: step, steps: [], reactions: [] });
    } else if (reaction !== undefined) reaction.steps.push(step);
    else if (policy.checks && step.op === "table_check") {
      // A check with nothing ahead of it in its segment (a GM "hold next" that is itself the check)
      // holds in place rather than opening an empty segment.
      if (current.steps.length === 0 && current.hold === undefined) segments[segments.length - 1] = { ...current, hold: step, steps: [step] };
      else segments.push({ index: segments.length, hold: step, steps: [step], reactions: [] });
    } else current.steps.push(step);
  }
  return segments;
}

/**
 * The dice key segment `index` of resolution `id` registers its throw under: `id#n`. Segment 0's
 * throw is `id#0`; the bare `id` is what COMMS holds a card on, which `CombatApp` registers as
 * segment 0's throw plus every hold's release.
 */
export function segmentThrowKey(id: string, index: number): string {
  return `${id}#${index}`;
}

/** The release key of the hold that opens segment `index` of resolution `id`. */
export function holdKey(id: string, index: number): string {
  return `${id}#hold#${index}`;
}

/** The dice key a reaction's own roll (Rebound's d6) throws under: `id#r<n>`. */
export function reactionThrowKey(id: string, index: number): string {
  return `${id}#r${index}`;
}
